import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { apiService } from '../../services/api';
import './AdminDashboard.css';

interface AdminUser {
  userId: string;
  cognitoUsername: string;
  username: string;
  email: string;
  pool: 'personal' | 'business';
  poolId: string;
  tier: string;
  storageGB: number;
  shares: number;
  crates: number;
  secrets: number;
  enabled: boolean;
}

interface ActivityItem {
  timestamp: string;
  action: string;
  details: string;
}

interface Stats {
  total: number;
  free: number;
  paid: number;
}

const ACTION_LABELS: Record<string, string> = {
  CREATE_CRATE: 'Created Crate',
  DELETE_CRATE: 'Deleted Crate',
  UPLOAD_FILE:  'Uploaded File',
  DELETE_FILE:  'Deleted File',
  CREATE_SHARE: 'Created Share',
  REVOKE_SHARE: 'Revoked Share',
  CREATE_SECRET: 'Created Secret',
  DELETE_SECRET: 'Deleted Secret',
};

function formatTimestamp(ts: string): string {
  try {
    return new Date(ts).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return ts;
  }
}

function sumGB(users: AdminUser[]): number {
  return users.reduce((acc, u) => acc + u.storageGB, 0);
}

function sumShares(users: AdminUser[]): number {
  return users.reduce((acc, u) => acc + u.shares, 0);
}

function sumCrates(users: AdminUser[]): number {
  return users.reduce((acc, u) => acc + u.crates, 0);
}

function sumSecrets(users: AdminUser[]): number {
  return users.reduce((acc, u) => acc + u.secrets, 0);
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [stats, setStats] = useState<Stats>({ total: 0, free: 0, paid: 0 });
  const [activeTab, setActiveTab] = useState<'personal' | 'business' | 'admins'>('personal');
  const [search, setSearch] = useState('');

  const [activityModal, setActivityModal] = useState<{ userId: string; username: string } | null>(null);
  const [activityData, setActivityData] = useState<ActivityItem[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);

  const [mfaConfirm, setMfaConfirm] = useState<AdminUser | null>(null);
  const [mfaWorking, setMfaWorking] = useState(false);
  const [mfaMessage, setMfaMessage] = useState<{ text: string; ok: boolean } | null>(null);

  useEffect(() => {
    checkAdminAndLoad();
  }, []);

  const checkAdminAndLoad = async () => {
    try {
      const session = await fetchAuthSession();
      const groups = (session.tokens?.idToken?.payload?.['cognito:groups'] ?? []) as string[];
      if (!groups.includes('Administrators')) {
        navigate('/dashboard');
        return;
      }
      await loadUsers();
    } catch {
      navigate('/admin-login');
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.adminGetUsers();
      setUsers(data.users as AdminUser[]);
      setStats(data.stats);
    } catch (e: any) {
      setError(e.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const openActivity = useCallback(async (user: AdminUser) => {
    setActivityModal({ userId: user.userId, username: user.username || user.cognitoUsername });
    setActivityData([]);
    setActivityLoading(true);
    try {
      const data = await apiService.adminGetUserActivity(user.userId, 10);
      setActivityData(data.activity);
    } catch {
      setActivityData([]);
    } finally {
      setActivityLoading(false);
    }
  }, []);

  const handleRemoveMfa = async () => {
    if (!mfaConfirm) return;
    setMfaWorking(true);
    try {
      await apiService.adminRemoveMfa(mfaConfirm.userId, mfaConfirm.pool);
      setMfaMessage({ text: `MFA removed for ${mfaConfirm.username || mfaConfirm.cognitoUsername}`, ok: true });
    } catch (e: any) {
      setMfaMessage({ text: e.message || 'Failed to remove MFA', ok: false });
    } finally {
      setMfaWorking(false);
      setMfaConfirm(null);
    }
  };

  const adminUsers = users.filter(u => u.tier === 'Admin');
  const personalUsers = users.filter(u => u.pool === 'personal' && u.tier !== 'Admin');
  const personalFree = personalUsers.filter(u => u.tier === 'Free').length;
  const personalPaid = personalUsers.filter(u => u.tier !== 'Free').length;

  const businessUsers = users.filter(u => u.pool === 'business');
  const businessPro = businessUsers.filter(u => u.tier === 'Pro').length;
  const businessEnterprise = businessUsers.filter(u => u.tier === 'Enterprise').length;

  const activeUsers = activeTab === 'business' ? businessUsers : activeTab === 'admins' ? adminUsers : personalUsers;
  const filtered = activeUsers.filter(u =>
    !search ||
    u.username.toLowerCase().includes(search.toLowerCase()) ||
    u.cognitoUsername.toLowerCase().includes(search.toLowerCase()) ||
    u.tier.toLowerCase().includes(search.toLowerCase())
  );

  const UserTable = () => (
    <div className="admin-table-wrapper">
      <table className="admin-table">
        <thead>
          <tr>
            <th>Username</th>
            <th>Tier</th>
            <th>Crates</th>
            <th>Storage (GB)</th>
            <th>Shares</th>
            <th>Secrets</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 && (
            <tr><td colSpan={7} className="admin-empty">No users found</td></tr>
          )}
          {filtered.map(user => (
            <tr key={user.userId} className={!user.enabled ? 'admin-row-disabled' : ''}>
              <td className="admin-cell-username">{user.username || user.cognitoUsername}</td>
              <td>
                <span className={`admin-badge admin-badge-tier-${user.tier.toLowerCase()}`}>{user.tier}</span>
              </td>
              <td className="admin-cell-num">{user.crates}</td>
              <td className="admin-cell-num">{user.storageGB.toFixed(3)}</td>
              <td className="admin-cell-num">{user.shares}</td>
              <td className="admin-cell-num">{user.secrets}</td>
              <td className="admin-cell-actions">
                <button className="admin-btn admin-btn-activity" onClick={() => openActivity(user)}>
                  Activity
                </button>
                <button
                  className="admin-btn admin-btn-mfa"
                  onClick={() => { setMfaConfirm(user); setMfaMessage(null); }}
                >
                  Remove MFA
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div className="admin-header-left">
          <button className="admin-back-btn" onClick={() => navigate('/dashboard')}>← Dashboard</button>
          <h1 className="admin-title">Admin Panel</h1>
        </div>
        <button className="admin-refresh-btn" onClick={loadUsers} disabled={loading}>
          {loading ? 'Loading…' : '↻ Refresh'}
        </button>
      </div>

      {mfaMessage && (
        <div className={`admin-toast ${mfaMessage.ok ? 'admin-toast-ok' : 'admin-toast-err'}`}>
          {mfaMessage.text}
          <button onClick={() => setMfaMessage(null)}>✕</button>
        </div>
      )}

      {/* Tabs */}
      <div className="admin-tabs">
        <button
          className={`admin-tab ${activeTab === 'personal' ? 'admin-tab-active' : ''}`}
          onClick={() => { setActiveTab('personal'); setSearch(''); }}
        >
          Personal Users
          {personalUsers.length > 0 && (
            <span className="admin-tab-count">{personalUsers.length}</span>
          )}
        </button>
        <button
          className={`admin-tab ${activeTab === 'business' ? 'admin-tab-active' : ''}`}
          onClick={() => { setActiveTab('business'); setSearch(''); }}
        >
          Business Users
          {businessUsers.length > 0 && (
            <span className="admin-tab-count">{businessUsers.length}</span>
          )}
        </button>
        <button
          className={`admin-tab ${activeTab === 'admins' ? 'admin-tab-active' : ''}`}
          onClick={() => { setActiveTab('admins'); setSearch(''); }}
        >
          Admins
          {adminUsers.length > 0 && (
            <span className="admin-tab-count admin-tab-count-admin">{adminUsers.length}</span>
          )}
        </button>
      </div>

      {/* Stats */}
      {activeTab === 'personal' && (
        <div className="admin-stats">
          <div className="admin-stat-card">
            <div className="admin-stat-value">{personalUsers.length}</div>
            <div className="admin-stat-label">Personal Users</div>
          </div>
          <div className="admin-stat-card admin-stat-free">
            <div className="admin-stat-value">{personalFree}</div>
            <div className="admin-stat-label">Free</div>
          </div>
          <div className="admin-stat-card admin-stat-paid">
            <div className="admin-stat-value">{personalPaid}</div>
            <div className="admin-stat-label">Paid</div>
          </div>
          <div className="admin-stat-card admin-stat-crates">
            <div className="admin-stat-value">{sumCrates(personalUsers)}</div>
            <div className="admin-stat-label">Total Crates</div>
          </div>
          <div className="admin-stat-card admin-stat-storage">
            <div className="admin-stat-value">{sumGB(personalUsers).toFixed(2)}</div>
            <div className="admin-stat-label">Total GB</div>
          </div>
          <div className="admin-stat-card admin-stat-shares">
            <div className="admin-stat-value">{sumShares(personalUsers)}</div>
            <div className="admin-stat-label">Total Shares</div>
          </div>
          <div className="admin-stat-card admin-stat-secrets">
            <div className="admin-stat-value">{sumSecrets(personalUsers)}</div>
            <div className="admin-stat-label">Total Secrets</div>
          </div>
        </div>
      )}
      {activeTab === 'business' && (
        <div className="admin-stats">
          <div className="admin-stat-card">
            <div className="admin-stat-value">{businessUsers.length}</div>
            <div className="admin-stat-label">Business Users</div>
          </div>
          <div className="admin-stat-card admin-stat-pro">
            <div className="admin-stat-value">{businessPro}</div>
            <div className="admin-stat-label">Pro</div>
          </div>
          <div className="admin-stat-card admin-stat-enterprise">
            <div className="admin-stat-value">{businessEnterprise}</div>
            <div className="admin-stat-label">Enterprise</div>
          </div>
          <div className="admin-stat-card admin-stat-crates">
            <div className="admin-stat-value">{sumCrates(businessUsers)}</div>
            <div className="admin-stat-label">Total Crates</div>
          </div>
          <div className="admin-stat-card admin-stat-storage">
            <div className="admin-stat-value">{sumGB(businessUsers).toFixed(2)}</div>
            <div className="admin-stat-label">Total GB</div>
          </div>
          <div className="admin-stat-card admin-stat-shares">
            <div className="admin-stat-value">{sumShares(businessUsers)}</div>
            <div className="admin-stat-label">Total Shares</div>
          </div>
          <div className="admin-stat-card admin-stat-secrets">
            <div className="admin-stat-value">{sumSecrets(businessUsers)}</div>
            <div className="admin-stat-label">Total Secrets</div>
          </div>
        </div>
      )}
      {activeTab === 'admins' && (
        <div className="admin-stats">
          <div className="admin-stat-card admin-stat-admin-count">
            <div className="admin-stat-value">{adminUsers.length}</div>
            <div className="admin-stat-label">Admin Users</div>
          </div>
          <div className="admin-stat-card admin-stat-crates">
            <div className="admin-stat-value">{sumCrates(adminUsers)}</div>
            <div className="admin-stat-label">Total Crates</div>
          </div>
          <div className="admin-stat-card admin-stat-storage">
            <div className="admin-stat-value">{sumGB(adminUsers).toFixed(2)}</div>
            <div className="admin-stat-label">Total GB</div>
          </div>
          <div className="admin-stat-card admin-stat-shares">
            <div className="admin-stat-value">{sumShares(adminUsers)}</div>
            <div className="admin-stat-label">Total Shares</div>
          </div>
          <div className="admin-stat-card admin-stat-secrets">
            <div className="admin-stat-value">{sumSecrets(adminUsers)}</div>
            <div className="admin-stat-label">Total Secrets</div>
          </div>
        </div>
      )}

      {error && <div className="admin-error">{error}</div>}

      {/* User Table */}
      <div className="admin-table-section">
        <div className="admin-table-toolbar">
          <input
            className="admin-search"
            type="text"
            placeholder="Search by username or tier…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <span className="admin-user-count">{filtered.length} user{filtered.length !== 1 ? 's' : ''}</span>
        </div>
        {loading ? (
          <div className="admin-loading">Loading users…</div>
        ) : (
          <UserTable />
        )}
      </div>

      {/* Activity modal */}
      {activityModal && (
        <div className="admin-modal-overlay" onClick={() => setActivityModal(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Last 10 Activity — {activityModal.username}</h3>
              <button className="admin-modal-close" onClick={() => setActivityModal(null)}>✕</button>
            </div>
            <div className="admin-modal-body">
              {activityLoading && <div className="admin-loading">Loading…</div>}
              {!activityLoading && activityData.length === 0 && (
                <div className="admin-empty">No activity recorded yet</div>
              )}
              {!activityLoading && activityData.length > 0 && (
                <table className="admin-activity-table">
                  <thead>
                    <tr>
                      <th>Action</th>
                      <th>Details</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activityData.map((item, idx) => (
                      <tr key={idx}>
                        <td>{ACTION_LABELS[item.action] ?? item.action}</td>
                        <td className="admin-cell-details" title={item.details}>
                          {item.details ? (item.details.length > 40 ? item.details.slice(0, 40) + '…' : item.details) : '—'}
                        </td>
                        <td className="admin-cell-time">{formatTimestamp(item.timestamp)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MFA removal confirmation */}
      {mfaConfirm && (
        <div className="admin-modal-overlay" onClick={() => setMfaConfirm(null)}>
          <div className="admin-modal admin-modal-confirm" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Remove MFA</h3>
              <button className="admin-modal-close" onClick={() => setMfaConfirm(null)}>✕</button>
            </div>
            <div className="admin-modal-body">
              <p>Remove MFA (TOTP) from <strong>{mfaConfirm.username || mfaConfirm.cognitoUsername}</strong>?</p>
              <p className="admin-confirm-sub">The user will be able to log in without their authenticator app until they set up MFA again.</p>
              <div className="admin-confirm-actions">
                <button className="admin-btn-cancel" onClick={() => setMfaConfirm(null)} disabled={mfaWorking}>
                  Cancel
                </button>
                <button className="admin-btn-danger" onClick={handleRemoveMfa} disabled={mfaWorking}>
                  {mfaWorking ? 'Removing…' : 'Remove MFA'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
